import { useEffect, useRef } from 'react';
import type { Agent } from '@studio/models/agent';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { depot } from '#~/depot/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { clear, hide, post } from '#~/notifications/index.js';
import { MessageId } from './constants.js';
import { decideOfflineToast } from './watchdog-honesty.js';

/** Connection notices follow the same reactive status as the Studio interface. */
export const useBridgeConnectionNotifications = ({
  id,
  name,
  embedded
}: Pick<Agent, 'id' | 'name' | 'embedded'>) => {
  const status = useDepot((state) => state.agents[id]?.status);
  const startupAt = useRef(Date.now());
  const hasConnected = useRef(false);
  const announcedDisconnect = useRef(false);

  useEffect(() => {
    const messageId = `${MessageId}-${id}`;
    hide(messageId);

    if (embedded) {
      if (status === 'booting' || status === 'idle') {
        post({
          id: messageId,
          message: `${name} is idle due to a period of inactivity. Automatically starting...`,
          severity: 'warning',
          title: `${name} is Booting`
        });
      }
      return () => hide(messageId);
    }

    if (status === 'online') {
      hasConnected.current = true;
      clear(`agent.${id}.reconnecting`);
      // Initial connection and brief, unannounced interruptions are silent.
      if (announcedDisconnect.current) {
        post({
          id: messageId,
          message: `${name} successfully reconnected to your Studio.`,
          severity: 'success',
          title: `${name} Reconnected`
        });
      }
      announcedDisconnect.current = false;
    } else if (status === 'offline') {
      clear(`agent.${id}.reconnecting`);
      const decision = decideOfflineToast({
        hasEverConnected: hasConnected.current,
        timeSinceStartupMs: Date.now() - startupAt.current
      });
      // Own the entire delay here so status changes/unmount cancel every stage.
      const timer = setTimeout(
        () => {
          // Read current state: React may not have committed a status update yet.
          if (depot.getState().agents[id]?.status !== 'offline') return;
          announcedDisconnect.current = hasConnected.current;
          post({
            id: messageId,
            message: `${vendorName()} is unable to connect to ${name}. Please check your internet connection, or restart ${name}.`,
            actionable: true,
            severity: 'error',
            title: hasConnected.current ? `${name} Disconnected` : `Unable to connect to ${name}`
          });
        },
        (decision.kind === 'defer' ? decision.deferMs : 0) + 5_000
      );

      return () => {
        clearTimeout(timer);
        hide(messageId);
      };
    }

    return () => hide(messageId);
  }, [id, name, embedded, status]);

  return status;
};
